import { useState } from 'react';
import { AgentPageShell, FormCard, Field, inputCls } from '../../components/AgentPageShell';
import { AgentResult } from '../../components/AgentResult';
import { api, ApiError } from '../../lib/api';
import { formatMillimes, tndStringToMillimes } from '../../lib/money';
import type { InvokeResponseBase } from '../../lib/types';

interface OverdueLineForm {
  invoice_number: string;
  customer_name: string;
  customer_email: string;
  customer_address: string;
  issue_date: string;
  due_date: string;
  amount_ttc_tnd: string;
  amount_paid_tnd: string;
}

interface RecouvreurResult {
  campaign: {
    generated_at: string;
    total_customers: number;
    total_invoices: number;
    total_due_millimes: number;
    relances: Array<{
      customer_name: string;
      customer_email?: string;
      customer_address?: string;
      level: 'courtoise' | 'ferme' | 'mise_en_demeure' | 'precontentieux';
      channel: 'email' | 'courrier';
      subject: string;
      body_text: string;
      body_html?: string;
      total_due_millimes: number;
      invoices_count: number;
      max_days_overdue: number;
    }>;
    skipped_customers: Array<{ customer_name: string; reason: string; max_days_overdue: number }>;
  };
  aging_summary: {
    buckets: Record<'0-30' | '30-60' | '60-90' | '90+', { count: number; total_ttc_millimes: number }>;
    total_outstanding_millimes: number;
  };
}

const LEVEL_STYLE: Record<RecouvreurResult['campaign']['relances'][number]['level'], string> = {
  courtoise: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  ferme: 'bg-amber-100 text-amber-900 border-amber-300',
  mise_en_demeure: 'bg-orange-100 text-orange-900 border-orange-300',
  precontentieux: 'bg-red-100 text-red-900 border-red-300',
};

const LEVEL_LABEL: Record<RecouvreurResult['campaign']['relances'][number]['level'], string> = {
  courtoise: 'Courtoise',
  ferme: 'Ferme',
  mise_en_demeure: 'Mise en demeure',
  precontentieux: 'Pré-contentieux',
};

export function RecouvreurPage() {
  const today = new Date().toISOString().slice(0, 10);
  const [todayInput, setTodayInput] = useState('2026-05-04');
  const [sellerName, setSellerName] = useState('Sahel Textile Industries');
  const [sellerMf, setSellerMf] = useState('1234567A/M/A/000');
  const [preferredChannel, setPreferredChannel] = useState<'email' | 'courrier' | ''>('');
  const [invoices, setInvoices] = useState<OverdueLineForm[]>([
    {
      invoice_number: 'F-100',
      customer_name: 'Client Frais',
      customer_email: 'frais@example.tn',
      customer_address: '',
      issue_date: '2026-03-16',
      due_date: '2026-04-16',
      amount_ttc_tnd: '1500.000',
      amount_paid_tnd: '0.000',
    },
    {
      invoice_number: 'F-200',
      customer_name: 'Client Lent',
      customer_email: 'lent@example.tn',
      customer_address: '',
      issue_date: '2026-02-28',
      due_date: '2026-03-30',
      amount_ttc_tnd: '2000.000',
      amount_paid_tnd: '500.000',
    },
    {
      invoice_number: 'F-300',
      customer_name: 'Café Dorra',
      customer_email: '',
      customer_address: 'Avenue Habib Bourguiba, Tunis',
      issue_date: '2026-01-19',
      due_date: '2026-02-18',
      amount_ttc_tnd: '5000.000',
      amount_paid_tnd: '0.000',
    },
  ]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InvokeResponseBase | null>(null);

  function updateInvoice(idx: number, patch: Partial<OverdueLineForm>) {
    setInvoices((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }
  function addInvoice() {
    setInvoices((prev) => [
      ...prev,
      {
        invoice_number: '',
        customer_name: '',
        customer_email: '',
        customer_address: '',
        issue_date: today,
        due_date: today,
        amount_ttc_tnd: '0.000',
        amount_paid_tnd: '0.000',
      },
    ]);
  }
  function removeInvoice(idx: number) {
    setInvoices((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const payload: Record<string, unknown> = {
        today: todayInput,
        seller: { name: sellerName, matricule_fiscal: sellerMf },
        invoices: invoices.map((inv) => ({
          invoice_number: inv.invoice_number,
          customer_name: inv.customer_name,
          ...(inv.customer_email.trim() ? { customer_email: inv.customer_email.trim() } : {}),
          ...(inv.customer_address.trim() ? { customer_address: inv.customer_address.trim() } : {}),
          issue_date: inv.issue_date,
          due_date: inv.due_date,
          amount_ttc_millimes: tndStringToMillimes(inv.amount_ttc_tnd),
          amount_paid_millimes: tndStringToMillimes(inv.amount_paid_tnd),
        })),
      };
      if (preferredChannel) payload.preferred_channel = preferredChannel;

      const res = await api.invokeAgent('recouvreur', {
        payload,
        fiscal_reference_date: todayInput,
      });
      setResponse(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AgentPageShell
      title="Recouvreur"
      module="Facturation · Phase 1"
      model="Sonnet 4.6"
      description={
        <>
          Génère une campagne de relance multi-clients à partir d'un portefeuille de factures
          impayées. Niveau choisi par seuils (≤ 15j skip · 15-30 courtoise · 30-60 ferme · 60-90
          mise en demeure · &gt; 90 pré-contentieux), templates fr-TN avec citations COC Art. 269.
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormCard title="Cabinet émetteur & contexte">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Date du jour"><input type="date" className={inputCls} value={todayInput} onChange={(e) => setTodayInput(e.target.value)} required /></Field>
            <Field label="Canal préféré"><select className={inputCls} value={preferredChannel} onChange={(e) => setPreferredChannel(e.target.value as 'email' | 'courrier' | '')}>
              <option value="">Auto (email si dispo, sinon courrier)</option>
              <option value="email">Email</option>
              <option value="courrier">Courrier (LRAR)</option>
            </select></Field>
            <Field label="Raison sociale"><input className={inputCls} value={sellerName} onChange={(e) => setSellerName(e.target.value)} required /></Field>
            <Field label="Matricule fiscal"><input className={inputCls + ' font-mono'} value={sellerMf} onChange={(e) => setSellerMf(e.target.value.toUpperCase())} required /></Field>
          </div>
        </FormCard>

        <FormCard
          title={`Factures à analyser (${invoices.length})`}
          action={
            <button type="button" onClick={addInvoice} className="text-xs px-2 py-1 rounded bg-ink text-cream hover:opacity-90">
              + Ajouter une facture
            </button>
          }
        >
          <div className="space-y-3">
            {invoices.map((inv, idx) => (
              <div key={idx} className="border border-border rounded p-3 bg-cream/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-wider text-taupe">Facture #{idx + 1}</span>
                  {invoices.length > 1 && (
                    <button type="button" onClick={() => removeInvoice(idx)} className="text-xs text-red-700 hover:text-red-900">Supprimer</button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                  <Field label="N° facture"><input className={inputCls + ' font-mono'} value={inv.invoice_number} onChange={(e) => updateInvoice(idx, { invoice_number: e.target.value })} required /></Field>
                  <Field label="Client"><input className={inputCls} value={inv.customer_name} onChange={(e) => updateInvoice(idx, { customer_name: e.target.value })} required /></Field>
                  <Field label="Email client"><input className={inputCls} value={inv.customer_email} onChange={(e) => updateInvoice(idx, { customer_email: e.target.value })} /></Field>
                  <Field label="Adresse client"><input className={inputCls} value={inv.customer_address} onChange={(e) => updateInvoice(idx, { customer_address: e.target.value })} /></Field>
                  <Field label="Date émission"><input type="date" className={inputCls} value={inv.issue_date} onChange={(e) => updateInvoice(idx, { issue_date: e.target.value })} required /></Field>
                  <Field label="Date échéance"><input type="date" className={inputCls} value={inv.due_date} onChange={(e) => updateInvoice(idx, { due_date: e.target.value })} required /></Field>
                  <Field label="TTC (TND)"><input className={inputCls + ' font-mono'} value={inv.amount_ttc_tnd} onChange={(e) => updateInvoice(idx, { amount_ttc_tnd: e.target.value })} /></Field>
                  <Field label="Réglé (TND)"><input className={inputCls + ' font-mono'} value={inv.amount_paid_tnd} onChange={(e) => updateInvoice(idx, { amount_paid_tnd: e.target.value })} /></Field>
                </div>
              </div>
            ))}
          </div>
        </FormCard>

        <button type="submit" disabled={loading || invoices.length === 0} className="btn-primary">
          {loading ? 'Génération de la campagne…' : 'Générer la campagne'}
        </button>
      </form>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}

      {response && (
        <AgentResult
          response={response}
          renderResult={(result) => <RecouvreurResultView result={result as RecouvreurResult} />}
        />
      )}
    </AgentPageShell>
  );
}

function RecouvreurResultView({ result }: { result: RecouvreurResult }) {
  return (
    <div className="space-y-5">
      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Vue d'ensemble</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
          <Stat label="Clients en relance" value={String(result.campaign.total_customers)} />
          <Stat label="Factures" value={String(result.campaign.total_invoices)} />
          <Stat label="Total dû" value={formatMillimes(result.campaign.total_due_millimes)} highlight />
          <Stat label="Encours total" value={formatMillimes(result.aging_summary.total_outstanding_millimes)} />
        </div>
      </section>

      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Balance âgée</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
          {(['0-30', '30-60', '60-90', '90+'] as const).map((k) => {
            const b = result.aging_summary.buckets[k];
            return (
              <div key={k} className="p-2 rounded border border-border bg-white">
                <div className="text-xs uppercase tracking-wider text-taupe">{k} jours</div>
                <div className="font-mono text-lg">{b.count}</div>
                <div className="text-xs">{formatMillimes(b.total_ttc_millimes)}</div>
              </div>
            );
          })}
        </div>
      </section>

      {result.campaign.skipped_customers.length > 0 && (
        <section>
          <div className="text-xs uppercase tracking-wider text-taupe mb-2">Clients ignorés ({result.campaign.skipped_customers.length})</div>
          <ul className="text-sm text-taupe space-y-1">
            {result.campaign.skipped_customers.map((s, i) => (
              <li key={i}>· <span className="text-ink">{s.customer_name}</span> — {s.reason}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-3">
        <div className="text-xs uppercase tracking-wider text-taupe">Messages générés ({result.campaign.relances.length})</div>
        {result.campaign.relances.map((r, i) => (
          <details key={i} className="card !p-0 overflow-hidden border border-border">
            <summary className="cursor-pointer px-4 py-3 flex items-center justify-between gap-2 hover:bg-cream/40">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`text-xs px-2 py-0.5 rounded border uppercase tracking-wider ${LEVEL_STYLE[r.level]}`}>
                  {LEVEL_LABEL[r.level]}
                </span>
                <span className="text-xs uppercase tracking-wider text-taupe">{r.channel}</span>
                <span className="font-medium">{r.customer_name}</span>
                <span className="text-xs text-taupe">{r.invoices_count} facture{r.invoices_count > 1 ? 's' : ''} · {r.max_days_overdue} j retard max</span>
              </div>
              <div className="font-mono text-sm">{formatMillimes(r.total_due_millimes)}</div>
            </summary>
            <div className="px-4 py-3 border-t border-border bg-cream/20 space-y-2">
              <div>
                <span className="text-xs uppercase tracking-wider text-taupe">Sujet</span>
                <div className="font-medium">{r.subject}</div>
              </div>
              <div>
                <span className="text-xs uppercase tracking-wider text-taupe">Corps du message</span>
                <pre className="whitespace-pre-wrap text-sm font-sans bg-white border border-border rounded p-3 mt-1 leading-relaxed">{r.body_text}</pre>
              </div>
            </div>
          </details>
        ))}
      </section>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`p-3 rounded border text-center ${highlight ? 'border-gold bg-gold/10' : 'border-border bg-white'}`}>
      <div className="text-xs uppercase tracking-wider text-taupe">{label}</div>
      <div className={`font-mono text-lg ${highlight ? 'font-bold' : ''}`}>{value}</div>
    </div>
  );
}
